// Section 7 and 9: stock capacity = base + building bonuses, computed through the pipeline; and
// the building rules shared by production, decay, deltas and the api (stream M3a): limits, upkeep
// and repair costs, damage (fire floors), and cloth as a winter warmth substitute.
import type { Config } from '../../config';
import { RESOURCES } from '../../core/constants';
import { clamp } from '../../core/math';
import { invalidateMemo } from '../../core/memo';
import { BUILDING_MEMO, population } from '../../core/queries';
import type { BuildingLimit, Registry } from '../../core/registry';
import type {
  Building,
  BuildingTypeId,
  Delta,
  GameState,
  PhaseContext,
  ReadContext,
  Resource,
} from '../../core/types';
import { num } from './value';

export function capacities(ctx: ReadContext): Record<Resource, number> {
  const out = {} as Record<Resource, number>;
  for (const r of RESOURCES) out[r] = num(ctx, { kind: 'capacity', resource: r });
  return out;
}

export function recomputeCapacity(ctx: ReadContext, draft: GameState): void {
  const caps = capacities(ctx);
  for (const r of RESOURCES) draft.stocks[r].capacity = caps[r];
}

// ---------------------------------------------------------------- limits (Section 9)

/** How many of a building a settlement of `people` may have: none, fixed, or 1 per N people. */
export function limitCount(limit: BuildingLimit, people: number): number {
  switch (limit.kind) {
    case 'none':
      return Number.MAX_SAFE_INTEGER;
    case 'fixed':
      return limit.max;
    case 'per-people':
      // "1 per 30 people": one serves up to 30, a 31st person allows a second (at least one).
      return Math.max(1, Math.ceil(people / limit.people));
  }
}

export function buildingLimit(registry: Registry, state: GameState, type: BuildingTypeId): number {
  const base = limitCount(registry.buildings[type].limit, population(state));
  return base === Number.MAX_SAFE_INTEGER ? base : base + extraSites(state, type);
}

/**
 * Section 19 Discovery "clay bed": an effect may add sites for a building type
 * (`params['limit:<type>']`, E2). Counted while the effect has not expired (a permanent site found
 * at the end of a season can be queued in the next season's decision), so validation, the
 * steward and production agree.
 */
function extraSites(state: GameState, type: BuildingTypeId): number {
  const key = `limit:${type}`;
  let extra = 0;
  for (const e of state.activeEffects) if (e.untilTurn >= state.turn) extra += e.params[key] ?? 0;
  return extra;
}

// ---------------------------------------------------------------- costs (Section 9)

/** A building type's materials scaled by `share`, per resource. */
export function scaledMaterials(
  registry: Registry,
  type: BuildingTypeId,
  share: number,
): Partial<Record<Resource, number>> {
  const out: Partial<Record<Resource, number>> = {};
  for (const r of RESOURCES) {
    const n = registry.buildings[type].materials[r];
    if (n !== undefined && n > 0) out[r] = n * share;
  }
  return out;
}

/** Section 9: upkeep is 10% of the building's cost (its materials) every 8 seasons. */
export function upkeepCost(
  registry: Registry,
  cfg: Config,
  type: BuildingTypeId,
): Partial<Record<Resource, number>> {
  return scaledMaterials(registry, type, cfg.building.upkeepFraction);
}

/** Share of a building that a repair rebuilds: its missing condition (B-05). */
export function repairShare(b: Pick<Building, 'condition'>): number {
  return clamp((100 - b.condition) / 100, 0, 1);
}

/**
 * Section 9 repairs (B-05, M6-06): build points and materials in proportion to the damage. A
 * cancelled repair refunds a share of what it actually paid (`BuildOrder.materialsSpent`), so
 * the wave-2 floor at the cancel-refund share is gone.
 */
export function repairMaterials(
  registry: Registry,
  b: Pick<Building, 'type' | 'condition'>,
): Partial<Record<Resource, number>> {
  return scaledMaterials(registry, b.type, repairShare(b));
}

/** Materials missing from stock for a cost, or null when the stock covers it. */
export function missingMaterial(
  state: GameState,
  cost: Partial<Record<Resource, number>>,
): { resource: Resource; short: number } | null {
  for (const r of RESOURCES) {
    const need = cost[r] ?? 0;
    const have = state.stocks[r].amount;
    if (need > have + 1e-9) return { resource: r, short: need - have };
  }
  return null;
}

// ---------------------------------------------------------------- damage (Sections 9, 13, 19)

/**
 * Apply a `damageBuilding` delta. Fire returns a stone building to 60% condition and a wood one
 * to 20% (never raising it); sabotage, raids and other causes subtract `amount`. Emits
 * `building-damaged` and returns the building's onDamaged deltas for the caller to apply.
 */
export function damageBuilding(
  ctx: PhaseContext,
  delta: Extract<Delta, { kind: 'damageBuilding' }>,
): readonly Delta[] {
  const b = ctx.draft.buildings[delta.buildingId];
  if (b === undefined) return [];
  const def = ctx.registry.buildings[b.type];
  const cfg = ctx.cfg.building;
  const next =
    delta.cause === 'fire'
      ? Math.min(
          b.condition,
          def.material === 'stone' ? cfg.fireConditionStone : cfg.fireConditionWood,
        )
      : clamp(b.condition - Math.max(0, delta.amount), 0, 100);
  const amount = b.condition - next;
  if (amount <= 0) return [];
  b.condition = next;
  invalidateMemo(ctx.draft, BUILDING_MEMO);
  recomputeCapacity(ctx.live, ctx.draft);
  ctx.report.add({ kind: 'building-damaged', buildingId: b.id, amount, source: delta.source });
  return def.onDamaged(ctx.live, b, amount);
}

// ---------------------------------------------------------------- cloth (Section 7)

/**
 * Section 7: cloth is a winter warmth substitute, 1 cloth = 0.3 warmth. The warmth units the
 * cloth stock can cover (for the rationing phase's warmth resolution).
 */
export function clothWarmthCover(state: GameState, cfg: Config): number {
  return Math.max(0, state.stocks.cloth.amount) * cfg.needs.clothWarmth;
}

/** Cloth consumed to cover `warmth` units of warmth need (inverse of clothWarmthCover). */
export function clothForWarmth(cfg: Config, warmth: number): number {
  return cfg.needs.clothWarmth <= 0 ? 0 : Math.max(0, warmth) / cfg.needs.clothWarmth;
}
